import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LabelIcon from '@mui/icons-material/Label';
import { ACCOUNTING_API, EINVOICE_API, handleApiError } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface EInvoiceUserQueryModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

interface UserQueryResult {
  etiket: string;
  kamuKurulusu: boolean;
  kayitZamani: string;
  unvan: string;
  vergiTcKimlikNo: string;
}

interface EInvoiceIntegrationResponse {
  id: number;
  companyId: string;
  username: string;
  password: string;
  companyName: string;
  taxNumber: string;
  providerType: string;
  isActive: boolean;
}

const EInvoiceUserQueryModal: React.FC<EInvoiceUserQueryModalProps> = ({
  open,
  onClose,
  companyId
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const localizeKnownError = (message: string) => {
    if (isTr) return message;
    if (message === 'Bir hata oluştu') return 'An error occurred';
    return message;
  };
  const [vknTckn, setVknTckn] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UserQueryResult | null>(null);
  const [activeIntegration, setActiveIntegration] = useState<EInvoiceIntegrationResponse | null>(null);
  const [loadingIntegration, setLoadingIntegration] = useState(false);
  const [integrationError, setIntegrationError] = useState<string | null>(null);

  // Aktif entegrasyonu yükle
  useEffect(() => {
    if (open && companyId) {
      loadActiveIntegration();
    }
  }, [open, companyId]);

  const loadActiveIntegration = async () => {
    setLoadingIntegration(true);
    setIntegrationError(null);

    try {
      console.log('📥 [E-INVOICE USER QUERY] Aktif entegrasyon yükleniyor...', { companyId });

      // CompanyId JWT claim'inden çözülür; eski /list/{companyId} rotası kaldırıldı.
      const response = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_LIST(false));
      const result = response.data;
      const integrations = result.data || result;

      // Aktif entegrasyonu bul
      const active = integrations.find((int: EInvoiceIntegrationResponse) => int.isActive);

      if (!active) {
        setIntegrationError(tt('Aktif bir e-Fatura entegrasyonu bulunamadı. Lütfen önce entegrasyon ayarlarını yapınız.', 'No active e-Invoice integration found. Please configure integration settings first.'));
        console.warn('⚠️ [E-INVOICE USER QUERY] Aktif entegrasyon bulunamadı');
        return;
      }

      setActiveIntegration(active);
      console.log('✅ [E-INVOICE USER QUERY] Aktif entegrasyon yüklendi:', active.providerType);

    } catch (err) {
      console.error('❌ [E-INVOICE USER QUERY] Entegrasyon yüklenirken hata:', err);
      setIntegrationError(localizeKnownError(handleApiError(err)));
    } finally {
      setLoadingIntegration(false);
    }
  };

  const handleClose = () => {
    setVknTckn('');
    setError(null);
    setResult(null);
    setIntegrationError(null);
    onClose();
  };

  const validateVknTckn = (value: string): boolean => {
    // 10 veya 11 haneli sayı kontrolü
    const regex = /^[0-9]{10,11}$/;
    return regex.test(value);
  };

  const handleQuery = async () => {
    setError(null);
    setResult(null);

    // Entegrasyon kontrolü
    if (!activeIntegration) {
      setError(tt('Aktif entegrasyon bulunamadı. Lütfen önce entegrasyon ayarlarını yapınız.', 'No active integration found. Please configure integration settings first.'));
      return;
    }

    // Validasyon
    if (!vknTckn.trim()) {
      setError(tt('Lütfen VKN veya TCKN giriniz.', 'Please enter a Tax ID or National ID.'));
      return;
    }

    if (!validateVknTckn(vknTckn)) {
      setError(tt('VKN 10 haneli, TCKN 11 haneli olmalıdır ve sadece rakam içermelidir.', 'Tax ID must be 10 digits, National ID must be 11 digits, and only numbers are allowed.'));
      return;
    }

    setLoading(true);

    try {
      console.log('🔍 [E-INVOICE USER QUERY] Sorgulama başlatılıyor:', {
        vkn: vknTckn,
        provider: activeIntegration.providerType
      });

      // ✅ EDM / PavoPay: yeni einvoice servisi üzerinden mükellef sorgusu.
      // Credential backend'de SQL'den çözülür; frontend kullanıcı/şifre göndermez.
      const provider = (activeIntegration.providerType || '').toLowerCase();
      if (provider !== 'finansbank' && provider !== 'qnb') {
        try {
          const resp = await einvoiceApi.post(EINVOICE_API.EDM_MUKELLEF_SORGULA, { vknTckn });
          const data = resp.data;
          console.log('✅ [E-INVOICE USER QUERY] Entegratör yanıtı:', data);

          if (data?.found && data?.isEInvoiceUser) {
            const users = Array.isArray(data.users) ? data.users : [];
            const first = users[0] || {};
            setResult({
              etiket: first.alias || '-',
              kamuKurulusu: false,
              kayitZamani: first.aliasCreationTime || '',
              unvan: first.title || data.title || '-',
              vergiTcKimlikNo: vknTckn,
            });
          } else {
            setError(tt(
              'Bu VKN/TCKN e-Fatura mükellefi değil. E-Arşiv Fatura düzenlemeniz gerekmektedir.',
              'This Tax ID/National ID is not an e-Invoice taxpayer. You need to issue an e-Archive Invoice.'
            ));
          }
        } catch (err: any) {
          console.error('❌ [E-INVOICE USER QUERY] Entegratör sorgu hatası:', err);
          const respData = err?.response?.data;
          setError(respData?.error || localizeKnownError(handleApiError(err)));
        } finally {
          setLoading(false);
        }
        return;
      }

      // ✅ Finansbank (QNB) — eski akış: .env'den API URL'sini al
      const eInvoiceApiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!eInvoiceApiUrl) {
        setError(tt('E-Fatura API URL tanımlı değil. Lütfen .env dosyasında REACT_APP_EINVOICE_URL ayarlayın.', 'E-Invoice API URL is not configured. Please set REACT_APP_EINVOICE_URL in the .env file.'));
        setLoading(false);
        return;
      }
      
      // QNBESolutionsController endpoint'i (GET)
      const response = await fetch(
        `${eInvoiceApiUrl}/QNBESolutions/EFaturaKullaniciBilgisi?username=${encodeURIComponent(activeIntegration.username)}&password=${encodeURIComponent(activeIntegration.password)}&sorguVkn=${encodeURIComponent(vknTckn)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.Error || errorData.message || tt('Sorgulama başarısız oldu', 'Query failed'));
      }

      const responseData = await response.json();
      console.log('✅ [E-INVOICE USER QUERY] API Response:', responseData);
      
      // QNBESolutionsController response formatı kontrol et (camelCase)
      if (responseData.success && responseData.eFaturaMukellefMi && responseData.kullanicilar && responseData.kullanicilar.length > 0) {
        // İlk kullanıcıyı al
        setResult(responseData.kullanicilar[0]);
        console.log('✅ [E-INVOICE USER QUERY] Kullanıcı bulundu:', responseData.kullanicilar[0]);
      } else if (responseData.success && !responseData.eFaturaMukellefMi) {
        setError(tt('Bu VKN/TCKN e-Fatura mükellefi değil. E-Arşiv Fatura düzenlemeniz gerekmektedir.', 'This Tax ID/National ID is not an e-Invoice taxpayer. You need to issue an e-Archive Invoice.'));
      } else {
        setError(tt('Kullanıcı bulunamadı veya e-Fatura mükellefi değil', 'User not found or not an e-Invoice taxpayer'));
      }

    } catch (err) {
      console.error('❌ [E-INVOICE USER QUERY] Hata:', err);
      setError(localizeKnownError(handleApiError(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading) {
      handleQuery();
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    
    try {
      // API'den gelen format: yyyyMMddHHmmss (örn: 20240514100124)
      if (dateString.length === 14) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        const hour = dateString.substring(8, 10);
        const minute = dateString.substring(10, 12);
        const second = dateString.substring(12, 14);
        
        const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
        return new Intl.DateTimeFormat(localeCode, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date);
      }
      
      // Normal tarih formatı
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(localeCode, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonSearchIcon />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {tt('E-Fatura Kayıtlı Kullanıcı Sorgulama', 'E-Invoice Registered User Query')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        {/* Entegrasyon Yükleniyor */}
        {loadingIntegration && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {tt('Aktif entegrasyon bilgileri yükleniyor...', 'Loading active integration details...')}
            </Typography>
          </Box>
        )}

        {/* Entegrasyon Hatası */}
        {integrationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {integrationError}
          </Alert>
        )}

        {/* Ana İçerik - Entegrasyon yüklendikten sonra göster */}
        {!loadingIntegration && activeIntegration && (
          <>
            {/* Aktif Entegrasyon Bilgisi */}
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                ✅ {tt('Aktif Entegrasyon', 'Active Integration')}: {activeIntegration.providerType.toUpperCase()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tt('Kullanıcı', 'User')}: {activeIntegration.username}
              </Typography>
            </Alert>

            {/* Bilgilendirme */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                📋 {tt('Kullanım Amacı', 'Purpose')}
              </Typography>
              <Typography variant="body2">
                {tt('Fatura alıcısının e-Fatura mükellefi olup olmadığını sorgulayabilirsiniz. Eğer mükellef değilse, e-Arşiv Fatura olarak düzenlemeniz gerekmektedir.', 'You can query whether the invoice recipient is an e-Invoice taxpayer. If not, you must issue it as an e-Archive Invoice.')}
              </Typography>
            </Alert>
          </>
        )}

        {/* Entegrasyon yoksa içeriği gösterme */}
        {!loadingIntegration && !activeIntegration && !integrationError && (
          <Alert severity="warning">
            {tt('Aktif entegrasyon bulunamadı.', 'No active integration found.')}
          </Alert>
        )}

        {/* Sorgulama Alanı - Sadece aktif entegrasyon varsa göster */}
        {!loadingIntegration && activeIntegration && (
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8f9fa', mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label={tt('VKN veya TCKN', 'Tax ID or National ID')}
                  value={vknTckn}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, ''); // Sadece rakam
                    if (value.length <= 11) {
                      setVknTckn(value);
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder={tt('10 haneli VKN veya 11 haneli TCKN', '10-digit Tax ID or 11-digit National ID')}
                  variant="outlined"
                  disabled={loading}
                  helperText={`${vknTckn.length}/11 ${tt('karakter', 'characters')}`}
                  inputProps={{
                    maxLength: 11,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={handleQuery}
                  disabled={loading || !vknTckn.trim()}
                  sx={{ height: 56 }}
                >
                  {loading ? tt('Sorgulanıyor...', 'Querying...') : tt('Sorgula', 'Query')}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Sonuç */}
        {result && (
          <Paper elevation={3} sx={{ p: 3, backgroundColor: '#f0f7ff', border: '2px solid', borderColor: 'primary.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" fontSize="large" />
              <Typography variant="h6" fontWeight="bold" color="primary">
                {tt('Kullanıcı Bulundu', 'User Found')}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {/* Unvan */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <BusinessIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {tt('Ünvan', 'Title')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {result.unvan || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* VKN/TCKN */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LabelIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      VKN/TCKN
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {result.vergiTcKimlikNo || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Etiket */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LabelIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {tt('Etiket Adresi', 'Label Address')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {result.etiket || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Kayıt Zamanı */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CalendarTodayIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {tt('E-Fatura Kayıt Zamanı', 'E-Invoice Registration Time')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDate(result.kayitZamani)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Kamu Kuruluşu */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <BusinessIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {tt('Kuruluş Tipi', 'Organization Type')}
                    </Typography>
                    <Chip
                      label={result.kamuKurulusu ? tt('Kamu Kuruluşu', 'Public Institution') : tt('Özel Sektör', 'Private Sector')}
                      color={result.kamuKurulusu ? 'warning' : 'success'}
                      size="small"
                      icon={result.kamuKurulusu ? <BusinessIcon /> : <CheckCircleIcon />}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Önemli Not */}
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>⚠️ {tt('Önemli', 'Important')}:</strong> {tt('Kayıt zamanından', 'You cannot issue an e-Invoice for a date')} <strong>{tt('önceki', 'before')}</strong> {tt('bir tarihe e-Fatura düzenlenemez. Bu durumda e-Arşiv Fatura düzenlemeniz gerekmektedir.', 'the registration time. In this case, you must issue an e-Archive Invoice.')}
              </Typography>
            </Alert>
          </Paper>
        )}

        {/* Henüz sorgulama yapılmadıysa - Sadece aktif entegrasyon varsa göster */}
        {!loadingIntegration && activeIntegration && !result && !error && !loading && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}>
            <PersonSearchIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {tt('VKN veya TCKN girerek sorgulama yapabilirsiniz', 'You can run a query by entering a Tax ID or National ID')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, backgroundColor: 'grey.50' }}>
        <Button onClick={handleClose} color="inherit">
          {tt('Kapat', 'Close')}
        </Button>
        {result && (
          <Button 
            onClick={() => {
              setResult(null);
              setVknTckn('');
            }} 
            variant="outlined" 
            color="primary"
          >
            {tt('Yeni Sorgulama', 'New Query')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EInvoiceUserQueryModal;

